import { useRef } from 'react';
import { Alert, Card, CardContent, CardHeader, CardTitle, ConfirmActionModal, LoadingSpinner, QueryErrorAlert } from '@shared/components';
import type { SageMappingRowEditor } from '../../hooks/useSageMappingRowEditor';
import type { DealerSageMappings as DealerSageMappingsData } from '../../types/dealerSageMappings.types';
import { useRestoreRowFocus } from '../../hooks/useRestoreRowFocus';
import { buildSageTableRows, sageCodeInputId } from '../../utils/sageMappingRows';
import { DealerSageMappingDataTable } from './DealerSageMappingDataTable';

interface Props {
    mappings: DealerSageMappingsData | undefined;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    includesOwnedDealers: boolean;
    editor: SageMappingRowEditor;
}

const HELP =
    'Set the Sage account code for each product this dealer sells. ' +
    'These codes are stored for reconciliation only; nothing is sent to Sage.';
const GROUP_NOTE = 'Codes here apply to agreements sold by this dealer itself. Each dealer it owns has its own codes.';
const EMPTY_NOTE = 'This dealer is not entitled to sell any products.';
const LOAD_ERROR = 'The Sage account codes could not be loaded.';

const mutedText = 'text-sm text-mp-muted';

function Body({ mappings, isLoading, isError, onRetry, includesOwnedDealers, editor }: Props) {
    if (isLoading) return <LoadingSpinner message="Loading Sage account codes" />;
    if (isError || !mappings) {
        return (
            <QueryErrorAlert message={LOAD_ERROR} onRetry={onRetry} />
        );
    }
    return (
        <div className="space-y-4">
            <p className={mutedText}>{HELP}</p>
            {includesOwnedDealers && <p className={mutedText}>{GROUP_NOTE}</p>}
            {editor.error && <Alert variant="error" role="alert">{editor.error}</Alert>}
            {mappings.products.length === 0 ? (
                <p className={mutedText}>{EMPTY_NOTE}</p>
            ) : (
                <DealerSageMappingDataTable rows={buildSageTableRows(mappings)} editor={editor} />
            )}
        </div>
    );
}

export function DealerSageMappings(props: Props) {
    const { editor } = props;
    const removal = editor.pendingRemoval;
    const code = removal?.nominal?.nominalCode ?? '';
    const focusFallback = useRef<HTMLDivElement>(null);
    useRestoreRowFocus({
        container: focusFallback,
        editingKey: editor.editingKey,
        isSaving: editor.isSaving,
        removalKey: removal?.productId ?? null,
        retryInputId: sageCodeInputId,
    });
    return (
        <Card>
            <CardHeader>
                <CardTitle>Sage account codes</CardTitle>
            </CardHeader>
            <CardContent>
                <div ref={focusFallback} tabIndex={-1} role="group" aria-label="Sage account codes" className="focus:outline-none">
                    <Body {...props} />
                </div>
            </CardContent>
            <ConfirmActionModal
                isOpen={removal !== null}
                title="Remove Sage account code"
                message={`Remove the Sage account code ${code} for ${removal?.label}?`}
                confirmLabel="Remove"
                confirmVariant="danger"
                isPending={editor.isRemoving}
                onConfirm={() => void editor.confirmRemoval()}
                onClose={editor.cancelRemoval}
            />
        </Card>
    );
}

